import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';

import { DealCellName } from '../../components/DealCellName/DealCellName';
import { DealCellStatus } from '../../components/DealCellStatus/DealCellStatus';
import { DealCellType } from '../../components/DealCellType/DealCellType';
import { NumericValue, NumericValueType } from '../../components/NumericValue/NumericValue';
import { ValueOrDash } from '../../components/ValueOrDash/ValueOrDash';
import { DealActionsMenuContainer } from '../../containers/DealActionsMenuContainer/DealActionsMenuContainer';
import { addSettingsToDecl, arrangeInOrder } from '../../service/DeclarationHelper';
import { ColumnsSettings, ColumnsSettingsViewDict, deals } from '../../typings/common';
import { Deal, DealItemAction, dealStatus } from '../../typings/deals';
import { CompleteReason } from '../../typings/graphql-grid';
import { GridCellProps } from '../../typings/grid';

import * as keyset from './deals.i18n';

const i18n = i18nFactory(keyset);

export enum DealsColumnKey {
    DEAL_TYPE = 'DEAL_TYPE',
    SETTINGS = 'SETTINGS',
    NAME = 'NAME',
    STATUS = 'STATUS',
    SPENT = 'SPENT',
    SHOWS = 'SHOWS',
    MIN_PRICE = 'MIN_PRICE',
    CPM = 'CPM',
    CLICKS = 'CLICKS',
    CPC = 'CPC',
    CTR = 'CTR'
}

interface DealsColumnDeclBase {
    key: DealsColumnKey;
    title: string;
    hintInfo?: string;
    isSortable?: boolean;
    maxWidth?: number;
    fixed?: boolean;
    minWidth?: number;
    cellClass?: string;
    CellComponent: React.MemoExoticComponent<(props: GridCellProps<Deal>) => JSX.Element | null>;
}

export interface DealsColumnDecl extends DealsColumnDeclBase {
    width: number;
    hidden?: boolean;
}

function getFixedDealsColumns(): Array<DealsColumnDeclBase> {
    return [
        {
            key: DealsColumnKey.DEAL_TYPE,
            title: i18n(DealsColumnKey.DEAL_TYPE),
            fixed: true,
            minWidth: 105,
            CellComponent: React.memo(
                ({ rowData: deal }: GridCellProps<Deal>): JSX.Element => <DealCellType type={deal.dealType} />
            )
        },
        {
            key: DealsColumnKey.SETTINGS,
            title: '',
            fixed: true,
            minWidth: 30,
            maxWidth: 30,
            cellClass: 'settings-cell',
            isSortable: false,
            CellComponent: React.memo(
                ({ rowIndex, actions }: GridCellProps<Deal, DealItemAction>): JSX.Element => (
                    <DealActionsMenuContainer index={rowIndex} actions={actions} />
                )
            )
        },
        {
            key: DealsColumnKey.NAME,
            title: i18n(DealsColumnKey.NAME),
            fixed: true,
            minWidth: 150,
            CellComponent: React.memo(
                ({ rowData: deal }: GridCellProps<Deal>): JSX.Element => (
                    <DealCellName
                        id={deal.id}
                        name={deal.name}
                        numberOfLinkedCampaigns={deal.numberOfLinkedCampaigns}
                    />
                )
            )
        }
    ];
}

function getDealsColumnsBase(): Array<DealsColumnDeclBase> {
    return [
        {
            key: DealsColumnKey.STATUS,
            title: i18n(DealsColumnKey.STATUS),
            minWidth: 150,
            CellComponent: React.memo(
                ({ rowData: deal }: GridCellProps<Deal>): JSX.Element => (
                    <DealCellStatus
                        status={dealStatus[deal.status]}
                        completeReason={deal.completeReason ? CompleteReason[deal.completeReason] : undefined}
                    />
                )
            )
        },
        {
            key: DealsColumnKey.SPENT,
            hintInfo: i18n('without-nds'),
            title: i18n(DealsColumnKey.SPENT),
            minWidth: 120,
            CellComponent: React.memo(
                ({ rowData: deal }: GridCellProps<Deal>): JSX.Element => (
                    <ValueOrDash value={deal.spent}>
                        <NumericValue
                            value={deal.spent}
                            type={NumericValueType.PRICE}
                            currencyCode={deal.currencyCode}
                        />
                    </ValueOrDash>
                )
            )
        },
        {
            key: DealsColumnKey.SHOWS,
            title: i18n(DealsColumnKey.SHOWS),
            minWidth: 100,
            CellComponent: React.memo(
                ({ rowData: deal }: GridCellProps<Deal>): JSX.Element => (
                    <ValueOrDash value={deal.shows}>
                        <NumericValue value={deal.shows} />
                    </ValueOrDash>
                )
            )
        },
        {
            key: DealsColumnKey.MIN_PRICE,
            title: i18n(DealsColumnKey.MIN_PRICE),
            minWidth: 150,
            CellComponent: React.memo(
                ({ rowData: deal }: GridCellProps<Deal>): JSX.Element => (
                    <ValueOrDash value={deal.minPrice}>
                        <NumericValue
                            value={deal.minPrice}
                            type={NumericValueType.PRICE}
                            currencyCode={deal.currencyCode}
                        />
                    </ValueOrDash>
                )
            )
        },
        {
            key: DealsColumnKey.CPM,
            title: i18n(DealsColumnKey.CPM),
            minWidth: 100,
            CellComponent: React.memo(
                ({ rowData: deal }: GridCellProps<Deal>): JSX.Element => (
                    <ValueOrDash value={deal.cpm}>
                        <NumericValue value={deal.cpm} type={NumericValueType.PRICE} currencyCode={deal.currencyCode} />
                    </ValueOrDash>
                )
            )
        },
        {
            key: DealsColumnKey.CLICKS,
            title: i18n(DealsColumnKey.CLICKS),
            minWidth: 100,
            CellComponent: React.memo(
                ({ rowData: deal }: GridCellProps<Deal>): JSX.Element => (
                    <ValueOrDash value={deal.clicks}>
                        <NumericValue value={deal.clicks} />
                    </ValueOrDash>
                )
            )
        },
        {
            key: DealsColumnKey.CPC,
            title: i18n(DealsColumnKey.CPC),
            minWidth: 100,
            CellComponent: React.memo(
                ({ rowData: deal }: GridCellProps<Deal>): JSX.Element => (
                    <ValueOrDash value={deal.cpc}>
                        <NumericValue value={deal.cpc} type={NumericValueType.PRICE} currencyCode={deal.currencyCode} />
                    </ValueOrDash>
                )
            )
        },
        {
            key: DealsColumnKey.CTR,
            title: i18n(DealsColumnKey.CTR),
            minWidth: 100,
            CellComponent: React.memo(
                ({ rowData: deal }: GridCellProps<Deal>): JSX.Element => (
                    <ValueOrDash value={deal.ctr}>
                        <NumericValue value={deal.ctr} type={NumericValueType.PERCENTAGE} />
                    </ValueOrDash>
                )
            )
        }
    ];
}

export const dealsColumnOrder = [
    DealsColumnKey.DEAL_TYPE,
    DealsColumnKey.SETTINGS,
    DealsColumnKey.NAME,
    DealsColumnKey.STATUS,
    DealsColumnKey.SPENT,
    DealsColumnKey.SHOWS,
    DealsColumnKey.MIN_PRICE,
    DealsColumnKey.CPM,
    DealsColumnKey.CLICKS,
    DealsColumnKey.CPC,
    DealsColumnKey.CTR
];

export function getDynamicDealsColumnsFields(): ColumnsSettings {
    return {
        columnOrder: dealsColumnOrder,
        columnViewInfo: {
            DEAL_TYPE: {
                width: 105,
                hidden: false
            },
            SETTINGS: {
                width: 30,
                hidden: false
            },
            NAME: {
                width: 250,
                hidden: false
            },
            STATUS: {
                width: 200,
                hidden: false
            },
            SPENT: {
                width: 150,
                hidden: false
            },
            SHOWS: {
                width: 150,
                hidden: false
            },
            MIN_PRICE: {
                width: 205,
                hidden: false
            },
            CPM: {
                width: 150,
                hidden: false
            },
            CLICKS: {
                width: 150,
                hidden: false
            },
            CPC: {
                width: 150,
                hidden: false
            },
            CTR: {
                width: 150,
                hidden: false
            }
        }
    };
}

export function getDealsColumns(columnsSettings: ColumnsSettingsViewDict): Array<DealsColumnDecl> {
    const fixedColumns = getFixedDealsColumns().map(column => {
        return addSettingsToDecl(column, columnsSettings, deals);
    });

    const result = getDealsColumnsBase().map(column => {
        return addSettingsToDecl(column, columnsSettings, deals);
    });

    return fixedColumns.concat(arrangeInOrder(result, columnsSettings[deals].columnOrder));
}
